import { motion } from "framer-motion";
import { Copy, Check, ExternalLink } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const CONTRACT_ADDRESS = "0x770dfE19F79506B724D094ad4CAC6a5dBb21F513";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_meme-coin-hub-10/artifacts/nddv30af_laughing-mojo-jojo.gif";
const UNISWAP_URL = "https://app.uniswap.org/swap?outputCurrency=0x770dfE19F79506B724D094ad4CAC6a5dBb21F513&chain=ethereum";
const ETHERSCAN_URL = "https://etherscan.io/token/0x770dfE19F79506B724D094ad4CAC6a5dBb21F513";

const Hero = () => {
  const [copied, setCopied] = useState(false);

  const copyContract = async () => {
    try {
      await navigator.clipboard.writeText(CONTRACT_ADDRESS);
      setCopied(true);
      toast.success("Contract address copied!", {
        description: "Now go ape into $MOJO",
        style: {
          background: "#0F0518",
          border: "1px solid rgba(157, 76, 221, 0.3)",
          color: "#FFFFFF",
        },
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  return (
    <section id="home" className="min-h-screen pt-24 pb-12 px-6 md:px-12 hero-gradient relative overflow-hidden">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-6rem)]">
        {/* Left Content */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="space-y-8"
        >
          <div className="space-y-4">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-[#9D4CDD] font-body text-xs tracking-[0.3em] uppercase"
            >
              The Most Villainous Token on Ethereum
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-none text-white"
            >
              WORLD
              <br />
              <span className="text-[#FFD700]">DOMINATION</span>
              <br />
              STARTS HERE
            </motion.h1>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-[#A199AA] font-body text-base leading-relaxed max-w-lg"
          >
            Mojo Jojo has finally found the key to defeating the Powerpuff Girls — 
            through the power of DeFi. Join the most evil monkey in crypto.
          </motion.p>

          {/* Contract Address Terminal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="terminal-block"
          >
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-[#A199AA] text-xs uppercase tracking-wider mb-1">Contract Address</p>
                <p className="text-[#FFD700] font-mono text-sm truncate" data-testid="contract-address">
                  {CONTRACT_ADDRESS}
                </p>
              </div>
              <button
                onClick={copyContract}
                className="flex-shrink-0 p-2 hover:bg-white/5 transition-colors"
                data-testid="copy-contract-btn"
              >
                {copied ? (
                  <Check size={20} className="text-green-400" />
                ) : (
                  <Copy size={20} className="text-[#9D4CDD]" />
                )}
              </button>
            </div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex flex-wrap gap-4"
          >
            <a
              href={UNISWAP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#FFD700] text-[#05000A] px-8 py-4 font-bold uppercase tracking-wide hover:bg-[#FFF066] hover:-translate-y-1 transition-all glow-yellow animate-pulse-glow"
              data-testid="hero-buy-btn"
            >
              Buy $MOJO
              <ExternalLink size={18} />
            </a>
            <a
              href={ETHERSCAN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-transparent text-white border-2 border-[#9D4CDD] px-8 py-4 font-bold uppercase tracking-wide hover:bg-[#9D4CDD]/10 transition-colors"
              data-testid="etherscan-btn"
            >
              View on Etherscan
              <ExternalLink size={18} />
            </a>
          </motion.div>
        </motion.div>

        {/* Right Content - Hero Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="relative flex items-center justify-center"
        >
          {/* Purple glow behind image */}
          <div className="absolute w-[80%] h-[80%] bg-[#9D4CDD]/20 rounded-full blur-[100px]" />
          
          <motion.img
            src={HERO_IMAGE}
            alt="Mojo Jojo - The Villainous Monkey"
            className="relative z-10 w-full max-w-md lg:max-w-lg animate-float drop-shadow-2xl"
            data-testid="hero-image"
            animate={{
              y: [0, -20, 0],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-[#9D4CDD]/50 rounded-full flex items-start justify-center p-2"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-2 bg-[#FFD700] rounded-full"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
